import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { SummaryBlock } from "@/components/summary/SummaryBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, Clock, Target, BarChart3, ArrowRight } from "lucide-react";
import { useCta } from "@/hooks/useCta";
import { useState } from "react";

export default function Audit() {
  const { handleCtaClick } = useCta();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const summary = "Audit Amazon 360° gratuit en 5-7 jours : analyse SP-API, diagnostic Ads, SEO, catalogue et plan d'action priorisé avec ROI estimé.";
  
  const bullets = [
    "Analyse complète via SP-API Amazon (performances, concurrence)",
    "Audit campagnes Ads (SP/SB/SD/DSP) et optimisations SEO",
    "Diagnostic santé catalogue et opportunités Buy Box",
    "Plan d'action priorisé avec ROI estimé par recommandation"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      // Redirect to audit page with Cal.com
      window.location.href = "/audit-gratuit";
    }, 2000);
  };

  return (
    <>
      <AdvancedSEOHead
        title="Audit Amazon Gratuit — Cosmos Wave | Diagnostic SP-API Complet"
        description="Audit Amazon 360° gratuit : analyse SP-API, diagnostic Ads & SEO, santé catalogue. Plan d'action priorisé avec ROI estimé en 5-7 jours."
        breadcrumbs={[
          { name: "Audit", url: "/audit" }
        ]}
        keywords={["audit Amazon gratuit", "diagnostic SP-API", "analyse Amazon", "audit Ads Amazon", "SEO Amazon gratuit"]}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <SummaryBlock summary={summary} bullets={bullets} className="summary-chip" />

          {/* Hero Section */}
          <section className="text-center py-16">
            <Badge variant="outline" className="mb-6 bg-green-500/10 text-green-600 border-green-500/20">
              100% Gratuit
            </Badge>
            <h1 className="text-5xl font-bold tracking-tight text-foreground mb-6">
              Audit Amazon
              <span className="block text-primary mt-2">Diagnostic 360° Gratuit</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Recevez un diagnostic complet de votre compte Amazon avec un plan d'action priorisé. 
              Analyse SP-API, Ads, SEO et catalogue en 5-7 jours ouvrés.
            </p>
          </section>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Form Section */}
            <div id="form">
              <Card className="p-8">
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl">Demander votre audit gratuit</CardTitle>
                  <p className="text-muted-foreground">Remplissez ce formulaire pour recevoir votre diagnostic personnalisé</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nom">Nom / Prénom *</Label>
                        <Input id="nom" name="nom" required />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input id="email" name="email" type="email" required />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="societe">Société</Label>
                      <Input id="societe" name="societe" />
                    </div>
                    
                    <div>
                      <Label htmlFor="marketplace">Marketplace(s) Amazon</Label>
                      <Input id="marketplace" name="marketplace" placeholder="Ex: Amazon.fr, Amazon.de..." />
                    </div>
                    
                    <div>
                      <Label htmlFor="ca_mensuel">CA mensuel Amazon approximatif</Label>
                      <Input id="ca_mensuel" name="ca_mensuel" placeholder="Ex: 50 000€/mois" />
                    </div>
                    
                    <div>
                      <Label htmlFor="objectifs">Objectifs principaux</Label>
                      <Textarea 
                        id="objectifs" 
                        name="objectifs" 
                        placeholder="Ex: Augmenter le ROAS, améliorer le ranking, optimiser la Buy Box..."
                        rows={4}
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>Envoi en cours...</>
                      ) : (
                        <>
                          Recevoir mon audit gratuit
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* What's Included */}
            <div className="space-y-8">
              <Card className="p-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Clock className="h-6 w-6 text-primary" />
                    Que comprend l'audit ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    "Analyse SP-API complète (performances, trends, concurrence)",
                    "Diagnostic campagnes Ads (SP/SB/SD) et optimisations",
                    "Audit SEO listings et opportunités de ranking",
                    "Évaluation santé catalogue et Buy Box", 
                    "Analyse concurrentielle et positionnement",
                    "Plan d'action priorisé avec ROI estimé"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="p-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Target className="h-6 w-6 text-primary" />
                    Délais & Livrables
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">1</div>
                    <div>
                      <div className="font-semibold">Questionnaire (1 jour)</div>
                      <div className="text-sm text-muted-foreground">Accès SP-API et informations compte</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">2</div>
                    <div>
                      <div className="font-semibold">Analyse (3-5 jours)</div>
                      <div className="text-sm text-muted-foreground">Diagnostic complet par nos experts</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">3</div>
                    <div>
                      <div className="font-semibold">Restitution (1 jour)</div>
                      <div className="text-sm text-muted-foreground">Rapport + call de présentation</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="text-center p-6 bg-muted/30 rounded-lg">
                <BarChart3 className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Audit 100% gratuit, sans engagement</h3>
                <p className="text-sm text-muted-foreground">
                  Diagnostic professionnel d'une valeur de 1500€ offert pour évaluer votre potentiel Amazon
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}